import { useId, useState, type ReactNode } from "react";
import { ListFilter } from "lucide-react";

import { FavoritesToggle } from "./FavoritesToggle";

/**
 * Chrome for a resource directory rendered inside the in-context slide-over.
 *
 * Layout mirrors the Django panel: a toolbar row with the filter disclosure on
 * the left and the "favourites only" toggle on the right, the result list in
 * the middle, and a footer carrying the result count on the left with an
 * escape hatch to the full directory on the right. Keeping the ♥ in the
 * always-visible toolbar means narrowing to favourites never requires opening
 * the filters first.
 *
 * Deliberately not a <details>: the ♥ is a button, and nesting one inside a
 * <summary> is broken for keyboard and screen-reader users. An explicit
 * aria-expanded/aria-controls disclosure keeps both controls reachable.
 */
export function DirectoryPanelShell({
  intro,
  filtersLabel,
  filtersOpenByDefault = false,
  filters,
  favorites,
  countLabel,
  footerAction,
  children,
}: {
  /** Optional lead-in line above the toolbar. */
  intro?: ReactNode;
  filtersLabel: string;
  /** Start expanded when a filter or favourites-only is already active. */
  filtersOpenByDefault?: boolean;
  filters: ReactNode;
  /** Omit to render no favourites toggle at all. */
  favorites?: {
    count: number;
    on: boolean;
    onChange: (value: boolean) => void;
    label: string;
  };
  countLabel: string;
  footerAction?: ReactNode;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(filtersOpenByDefault);
  const filtersId = useId();

  return (
    <div className="space-y-4">
      {intro}

      <div className="rounded-lg border border-border">
        <div className="flex items-center justify-between gap-3 p-3">
          <button
            type="button"
            aria-expanded={open}
            aria-controls={filtersId}
            onClick={() => setOpen((v) => !v)}
            className="inline-flex min-h-touch items-center gap-1.5 text-sm font-semibold text-foreground"
          >
            <ListFilter className="size-4" aria-hidden />
            {filtersLabel}
          </button>
          {favorites && (
            <FavoritesToggle
              count={favorites.count}
              on={favorites.on}
              onChange={favorites.onChange}
              label={favorites.label}
            />
          )}
        </div>
        {/* Kept mounted so aria-controls always resolves to a real element. */}
        <div id={filtersId} hidden={!open} className="border-t border-border p-3">
          {filters}
        </div>
      </div>

      <div className="space-y-4">{children}</div>

      <div className="flex items-center justify-between gap-3 border-t border-border pt-3">
        <span className="text-sm tabular-nums text-muted-foreground">{countLabel}</span>
        {footerAction}
      </div>
    </div>
  );
}
